import NodeStatus from 'features/Integrations/components/NodeStatus/NodeStatus';
import NodeStatusIcon from 'features/Integrations/features/ConnectedDevices/features/DeviceList/components/NodeStatusIcon/NodeStatusIcon';
import { usePerms } from 'contexts/PermsContext';
import { isBuoyDevice } from 'features/Integrations/features/ConnectedDevices/features/Buoy/utils/buoyDevice.utils';
import { hasDevicePermissionFromDevice } from 'features/Integrations/features/ConnectedSystems/utils/devicePermissions.utils';
import {
  openStageSettings,
  openDeviceSettings,
  openUSGSSettings,
} from 'features/Integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/Integrations/state/thunks';
import { DeviceIsSystemType } from 'features/Integrations/utils/deviceComparison.utils';
import { Accordion, Button } from 'react-bootstrap';
import { FaCog } from 'react-icons/fa';
import { useDispatch } from 'react-redux';

/**
 * @param {{devices: object[], setSelectedDevice: (index: number, id?: number), onOpenDevice: ()}}
 */
export default function DeviceRowAccordion({ onOpenDevice = () => {}, devices, widget = false }) {
  const perms = usePerms();
  const dispatch = useDispatch();

  const onSelectDevice = (deviceId) => {
    // Always select by device id to keep map and list in sync
    dispatch(setSelectedDevice({ index: -1, id: deviceId }));
  };

  const onSettingsClick = (e, device) => {
    e.stopPropagation();
    dispatch(setSelectedDevice({ index: -1, id: device.id }));

    if (DeviceIsSystemType(device, 'Flood')) {
      dispatch(openStageSettings(device.id));
    } else if (device.system_type === 'USGS') {
      dispatch(openUSGSSettings(device.id));
    } else {
      dispatch(openDeviceSettings({ id: device.id, deviceSettings: true }));
    }
  };

  const onRowClick = (device) => {
    // always update selection
    onSelectDevice(device.id);

    // open datasheets for AWARE and USGS devices
    if (
      DeviceIsSystemType(device, 'Flood') ||
      device.system_type === 'USGS' ||
      isBuoyDevice(device)
    ) {
      onOpenDevice(device.id);
      return;
    }

    // otherwise open settings
    if (!widget) dispatch(openDeviceSettings(device.id));
    else dispatch(openDeviceSettings(device.id));
  };

  return (
    <Accordion alwaysOpen className="w-100">
      {devices.map((device, index) => {
        const hasCreateEditPermission = hasDevicePermissionFromDevice(perms, device, 'createEdit');
        // Keep header concise; details live in the expanded body.
        const deviceName = `${device.static_id ? device.static_id + ' ' : ''}${device.alias || device.name}`;

        return (
          <Accordion.Item
            key={device.id || index}
            eventKey={String(index)}
            className="mb-2 border border-theme-strong rounded overflow-hidden"
          >
            <Accordion.Header>
              <div
                className="d-flex flex-grow-1 justify-content-between align-items-center gap-2 pe-4"
                style={{ minWidth: 0 }}
              >
                <div className="text-truncate">{deviceName}</div>
                <NodeStatusIcon device={device} />
              </div>
            </Accordion.Header>
            <Accordion.Body className="bg-primary">
              <div className="d-flex flex-column gap-2">
                {/* Full status pill is shown in the expanded panel. */}
                <div className="d-flex align-items-center gap-2">
                  <span className="small">Status:</span>
                  <NodeStatus device={device} />
                </div>
                <div className="small">Node ID: {device.node_id}</div>
                <div className="small">Type: {String(device.device_type).replace(/_/g, ' ')}</div>
                {device.system_type && <div className="small">System: {device.system_type}</div>}
                <div className="d-flex gap-2">
                  {(DeviceIsSystemType(device, 'Flood') ||
                    device.system_type === 'USGS' ||
                    isBuoyDevice(device)) && (
                    <Button variant="primary" className="btn-sm" onClick={() => onRowClick(device)}>
                      Open
                    </Button>
                  )}
                  {hasCreateEditPermission && (
                    <button
                      className="btn btn-secondary btn-sm d-flex align-items-center gap-1"
                      onClick={(e) => onSettingsClick(e, device)}
                    >
                      <FaCog size={14} />
                      Settings
                    </button>
                  )}
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
}
